import { useInputControl } from '@conform-to/react'
import {
	OTPInput,
	OTPInputContext,
	REGEXP_ONLY_DIGITS_AND_CHARS,
	type OTPInputProps,
} from 'input-otp'
import React, { useContext, useId } from 'react'
import { cn } from '#app/utils/misc.tsx'
import {
	Checkbox,
	CheckboxField as CatalystCheckboxField,
} from './catalyst/checkbox.tsx'
import {
	ErrorMessage,
	Field as CatalystField,
	Label,
} from './catalyst/fieldset.tsx'
import { Input } from './catalyst/input.tsx'

export type ListOfErrors = Array<string | null | undefined> | null | undefined

export function ErrorList({
	id,
	errors,
}: {
	errors?: ListOfErrors
	id?: string
}) {
	const errorsToRender = errors?.filter(Boolean)
	if (!errorsToRender?.length) return null
	return (
		// Rendered at form level, outside any Catalyst Field, so a Headless
		// Description is not appropriate here; match ErrorMessage's slot and
		// typography by hand instead.
		<ul data-slot="error" id={id} className="flex flex-col gap-1">
			{errorsToRender.map((e) => (
				<li
					key={e}
					className="text-base/6 text-red-600 sm:text-sm/6 dark:text-red-500"
				>
					{e}
				</li>
			))}
		</ul>
	)
}

function FieldErrors({
	id,
	errors,
	className,
}: {
	id: string
	errors: ListOfErrors
	className?: string
}) {
	const errorsToRender = errors?.filter(Boolean)
	if (!errorsToRender?.length) return null
	return (
		<ErrorMessage id={id} className={className}>
			{errorsToRender.map((e) => (
				<span key={e} className="block">
					{e}
				</span>
			))}
		</ErrorMessage>
	)
}

type InputProps = React.ComponentPropsWithoutRef<typeof Input>

export function Field({
	labelProps,
	inputProps,
	errors,
	className,
}: {
	labelProps: React.LabelHTMLAttributes<HTMLLabelElement>
	inputProps: React.InputHTMLAttributes<HTMLInputElement>
	errors?: ListOfErrors
	className?: string
}) {
	const fallbackId = useId()
	const id = inputProps.id ?? fallbackId
	const errorId = errors?.filter(Boolean).length ? `${id}-error` : undefined
	const { htmlFor: _htmlFor, ...restLabelProps } = labelProps
	// Conform's getInputProps returns a `key`; pass it explicitly rather than
	// spreading it (React warns when `key` arrives via spread).
	const { key, ...restInputProps } = inputProps as InputProps & {
		key?: React.Key
	}
	return (
		<CatalystField className={className}>
			<Label htmlFor={id} {...restLabelProps} />
			<Input
				key={key}
				{...restInputProps}
				id={id}
				invalid={Boolean(errorId)}
				aria-describedby={errorId}
			/>
			{errorId ? <FieldErrors id={errorId} errors={errors} /> : null}
		</CatalystField>
	)
}

function OTPSlot({ index }: { index: number }) {
	const context = useContext(OTPInputContext)
	const slot = context.slots[index]
	if (!slot) throw new Error('Invalid slot index')
	const { char, hasFakeCaret, isActive } = slot
	return (
		<div
			data-slot="otp-slot"
			className={cn(
				'relative flex size-10 items-center justify-center border-y border-r border-zinc-950/10 text-base text-zinc-950 first:rounded-l-lg first:border-l last:rounded-r-lg sm:text-sm dark:border-white/10 dark:bg-white/5 dark:text-white',
				isActive && 'z-10 ring-2 ring-blue-500',
			)}
		>
			{char}
			{hasFakeCaret ? (
				<div className="pointer-events-none absolute inset-0 flex items-center justify-center">
					<div className="animate-caret-blink h-4 w-px bg-zinc-950 dark:bg-white" />
				</div>
			) : null}
		</div>
	)
}

export function OTPField({
	labelProps,
	inputProps,
	errors,
	className,
}: {
	labelProps: React.LabelHTMLAttributes<HTMLLabelElement>
	inputProps: Partial<OTPInputProps & { render: never }>
	errors?: ListOfErrors
	className?: string
}) {
	const fallbackId = useId()
	const id = inputProps.id ?? fallbackId
	const errorId = errors?.filter(Boolean).length ? `${id}-error` : undefined
	const { htmlFor: _htmlFor, ...restLabelProps } = labelProps
	return (
		<CatalystField className={className}>
			<Label htmlFor={id} {...restLabelProps} />
			<OTPInput
				inputMode="text"
				pattern={REGEXP_ONLY_DIGITS_AND_CHARS}
				maxLength={6}
				containerClassName="mt-3 flex items-center gap-2 has-disabled:opacity-50"
				className="disabled:cursor-not-allowed"
				aria-invalid={errorId ? true : undefined}
				aria-describedby={errorId}
				{...inputProps}
				id={id}
			>
				<div className="flex items-center">
					<OTPSlot index={0} />
					<OTPSlot index={1} />
					<OTPSlot index={2} />
				</div>
				{/* Fixed separator between the two groups of three; an elastic
				    spacer would push the groups to opposite ends of the field. */}
				<div className="w-3" />
				<div className="flex items-center">
					<OTPSlot index={3} />
					<OTPSlot index={4} />
					<OTPSlot index={5} />
				</div>
			</OTPInput>
			{errorId ? (
				// Catalyst's Field only spaces an error that follows a
				// `[data-slot=control]` sibling, and OTPInput's container has no such
				// slot, so the gap has to be set here.
				<FieldErrors id={errorId} errors={errors} className="mt-3" />
			) : null}
		</CatalystField>
	)
}

export function CheckboxField({
	labelProps,
	buttonProps,
	errors,
	className,
}: {
	labelProps: React.ComponentProps<'label'>
	buttonProps: {
		name: string
		form: string
		value?: string
		id?: string
		key?: React.Key
		defaultChecked?: boolean
		disabled?: boolean
		onChange?: (checked: boolean) => void
	}
	errors?: ListOfErrors
	className?: string
}) {
	const { key, defaultChecked, name, form, value, onChange, ...rest } =
		buttonProps
	const fallbackId = useId()
	const checkedValue = value ?? 'on'
	const input = useInputControl({
		key,
		name,
		formId: form,
		initialValue: defaultChecked ? checkedValue : undefined,
	})
	const id = rest.id ?? fallbackId
	const errorId = errors?.filter(Boolean).length ? `${id}-error` : undefined
	const { htmlFor: _htmlFor, ...restLabelProps } = labelProps

	return (
		<div className={className}>
			<CatalystCheckboxField>
				<Checkbox
					id={id}
					disabled={rest.disabled}
					// Headless's Checkbox has no `invalid` prop (unlike its Input), and it
					// derives `aria-describedby` itself from the ErrorMessage registered on
					// the surrounding Field, so only `aria-invalid` is ours to set.
					aria-invalid={errorId ? true : undefined}
					checked={input.value === checkedValue}
					onChange={(checked) => {
						input.change(checked ? checkedValue : '')
						onChange?.(checked)
					}}
					onFocus={() => input.focus()}
					onBlur={() => input.blur()}
				/>
				<Label htmlFor={id} {...restLabelProps} />
				{errorId ? (
					// Catalyst's CheckboxField grid only places the control, label and
					// description slots; the error needs the label's column explicitly.
					<FieldErrors
						id={errorId}
						errors={errors}
						className="col-start-2 row-start-2"
					/>
				) : null}
			</CatalystCheckboxField>
		</div>
	)
}
